import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Button, Paper, Stack, Typography, TextField } from "@mui/material";

import { InfoApi } from "../../api/InfoApi";
import { OrgAdminApi } from "../../api/OrgAdminApi";
import BackgroundSelection from "../../components/leaderboardSetting/BackgroundSelection";
import GroupBySelection from "../../components/leaderboardSetting/GroupBySelection";
import ItemSelection from "../../components/leaderboardSetting/ItemSelection";
import ScopeSelection from "../../components/leaderboardSetting/ScopeSelection";
import { buildLeaderboard } from "../../helper/buildLeaderboard";

const LIST_PATH = "/user/leaderboardSettings";

const DISPLAY_RULES = [
  "最多顯示前 30 名",
  "同分同名次，例如 1、2、2、4",
  "第 1 名顯示「榜首」",
  "你的一行會被標示",
];

function asList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
}

function cardSx() {
  return {
    p: 2.5,
    borderRadius: 3,
    border: "1px solid #eef0f3",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  };
}

export default function UserLeaderboardSettingCreatePage() {
  const navigate = useNavigate();
  const [scope, setScope] = useState("organization");
  const [scopeArg, setScopeArg] = useState("_");
  const [groupBy, setGroupBy] = useState("member");
  const [item, setItem] = useState("duration");
  const [skillCode, setSkillCode] = useState("all");
  const [background, setBackground] = useState("yellow");
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    OrgAdminApi.getMyGrpListCallback((isSuccess, msg, data) => {
      setGroupsLoading(false);
      if (!isSuccess) {
        toast.error(typeof msg === "string" && msg ? msg : "讀取社列表失敗");
        setGroups([]);
        return;
      }
      setGroups(asList(data));
    });
  }, []);

  useEffect(() => {
    let active = true;
    InfoApi.getSkillList()
      .then((data) => {
        if (!active) {
          return;
        }
        const list = asList(data)
          .filter((skill) => String(skill?.code || "").trim() !== "")
          .sort((left, right) => {
            const position = Number(left.dashboard_position || 0) - Number(right.dashboard_position || 0);
            if (position !== 0) {
              return position;
            }
            return String(left.name_zh || left.code).localeCompare(String(right.name_zh || right.code), "zh");
          });
        setSkills(list);
        setSkillsLoading(false);
      })
      .catch(() => {
        if (!active) {
          return;
        }
        toast.error("讀取活動失敗");
        setSkills([]);
        setSkillsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const selectedSkill = skills.find((skill) => skill.code === skillCode);
  const built = useMemo(
    () =>
      buildLeaderboard({
        scope,
        scopeArg,
        title,
        groupBy,
        item,
        skillCode,
        skillName: selectedSkill?.name_zh || selectedSkill?.name_en || "",
        suffix: "",
      }),
    [scope, scopeArg, groupBy, item, skillCode, title, selectedSkill]
  );

  const handleCreate = () => {
    if (!built.ready || submitting) {
      return;
    }

    setSubmitting(true);
    OrgAdminApi.createLeaderboardSettingCallback(
      {
        leaderboard_code: built.code,
        title: built.title,
        display_pos: 0,
        background,
      },
      (isSuccess, msg) => {
        setSubmitting(false);
        if (!isSuccess) {
          toast.error(typeof msg === "string" && msg ? msg : "建立失敗");
          return;
        }
        toast.success("建立成功");
        navigate(LIST_PATH);
      }
    );
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#111827" }}>
          建立排行榜
        </Typography>
        <Box
          sx={{
            px: 1.25,
            py: 0.25,
            borderRadius: 999,
            bgcolor: "#e8f1ff",
            color: "#1d4ed8",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          本學年
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="text" onClick={() => navigate(LIST_PATH)}>
          返回列表
        </Button>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.7fr) minmax(280px, 0.9fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Stack spacing={2}>
        <Paper elevation={0} sx={{ ...cardSx(), px: 3, py: 3.5 }}>
          <ScopeSelection
            scope={scope}
            scopeArg={scopeArg}
            groups={groups}
            groupsLoading={groupsLoading}
            onChange={({ scope: nextScope, scopeArg: nextScopeArg }) => {
              setScope(nextScope);
              setScopeArg(nextScopeArg);
            }}
          />
          <GroupBySelection groupBy={groupBy} onChange={setGroupBy} />
          <ItemSelection
            item={item}
            skillCode={skillCode}
            skills={skills}
            skillsLoading={skillsLoading}
            onChange={({ item: nextItem, skillCode: nextSkillCode }) => {
              setItem(nextItem);
              setSkillCode(nextSkillCode);
            }}
          />
        </Paper>
        <Paper elevation={0} sx={cardSx()}>
            <Typography variant="body2" sx={{ color: "#6b7280", mb: 1.5 }}>
              排行榜標題建議
            </Typography>
            <Typography sx={{ fontWeight: 800, color: "#111827", fontSize: 18 }}>
              {built.headline} {built.metricLine}
            </Typography>
          </Paper>

        </Stack>

        
        <Stack spacing={2}>
          <Paper elevation={0} sx={cardSx()}>
            <BackgroundSelection value={background} onChange={setBackground} />
          </Paper>

          <Paper elevation={0} sx={cardSx()}>
              <Stack spacing={2}>
                <TextField
                  id="title"
                  name="title"
                  label="標題"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  fullWidth
                />
                
              </Stack>
            </Paper>

          <Paper elevation={0} sx={cardSx()}>
            <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
              排行榜代碼
            </Typography>
            <Box
              sx={{
                bgcolor: "#f3f4f6",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                color: "#6b7280",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                wordBreak: "break-all",
              }}
            >
              {built.code}
            </Box>
            {/* <pre> DEBUG: 
                title: {title} <br />
                built: {JSON.stringify(built, null, 2)} <br />
                
              </pre> */}
          </Paper>

          {/* <Paper elevation={0} sx={cardSx()}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>顯示方式</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#2563eb" }}>
              {DISPLAY_RULES.map((rule) => (
                <Typography
                  key={rule}
                  component="li"
                  variant="body2"
                  sx={{ color: "#1f2937", mb: 0.75, "&::marker": { color: "#2563eb" } }}
                >
                  {rule}
                </Typography>
              ))}
            </Box>
          </Paper> */}

          <Button
            fullWidth
            disabled={!built.ready || submitting}
            onClick={handleCreate}
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontWeight: 800,
              fontSize: 16,
              boxShadow: "none",
              bgcolor: "#2563eb",
              color: "#fff",
              "&:hover": { bgcolor: "#1d4ed8", boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" },
            }}
          >
            建立排行榜
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
